import { useEffect, useState } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useDoc } from "@/hooks/useDoc";
import { adapter } from "@/services/db";
import { uploadImage } from "@/services/storage";
import { COLLECTIONS } from "@/lib/brugoSchema";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { publishCms } from "@/services/cmsApi";
import { CmsPublishGate } from "@/components/cms/CmsPublishGate";
import { VersionsSheet } from "@/components/cms/VersionsSheet";
import { SiteContentEditor } from "@/components/cms/SiteContentEditor";
import { ImageField } from "@/components/cms/ImageField";
import { SPECS } from "@/lib/imageSpecs";

const empty = { hero: { image_url: "", title: "", subtitle: "", cta_text: "", cta_link: "" }, banners: [] };

export default function AppContent() {
  const { data } = useDoc(COLLECTIONS.config, "app_settings");
  const [state, setState] = useState(empty);
  const [ver, setVer] = useState(null);
  const { data: sysData } = useDoc("system_status", "global");
  const [showPros, setShowPros] = useState(true);
  const [audios, setAudios] = useState([{ key: "new_lead", label: "New Lead", url: "" }]);
  const [audioUp, setAudioUp] = useState("");

  useEffect(() => {
    if (!sysData) return;
    if (typeof sysData.show_pros_notified === "boolean") setShowPros(sysData.show_pros_notified);
    if (Array.isArray(sysData.pro_audio) && sysData.pro_audio.length) setAudios(sysData.pro_audio);
  }, [sysData]);

  useEffect(() => {
    if (data) setState({ hero: { ...empty.hero, ...(data.hero || {}) }, banners: data.banners || [] });
  }, [data]);

  const [gate, setGate] = useState(null);

  const save = () => setGate({
    title: "Publish Customer App content", description: "Writes config/app_settings (hero + banners). Requires the profile password and an OTP sent to",
    run: async (auth) => { await publishCms({ ...auth, target: "config", doc_id: "app_settings", data: state }); toast.success("Customer App content published"); },
  });

  const saveSys = () => setGate({
    title: "Publish live app settings & Pro sounds", description: "Writes system_status/global (pros-notified flag + Pro audio). Requires the profile password and an OTP sent to",
    run: async (auth) => { await publishCms({ ...auth, target: "system_status", doc_id: "global", data: { show_pros_notified: showPros, pro_audio: audios } }); toast.success("Live app settings published"); },
  });
  const uploadAudio = async (file, key) => {
    if (!file) return;
    setAudioUp(key);
    try {
      const url = await uploadImage(`app_content/audio/${key}_${Date.now()}_${file.name}`, file);
      setAudios((a) => a.map((x) => (x.key === key ? { ...x, url } : x)));
      toast.success("Audio uploaded — remember to Publish");
    } catch (e) { toast.error(e.code === "storage/unauthorized" ? "Storage denied — admin claim required" : e.message); }
    finally { setAudioUp(""); }
  };
  const addAudio = () => setAudios((a) => [...a, { key: "sound_" + Date.now(), label: "New sound", url: "" }]);
  const rmAudio = (key) => setAudios((a) => a.filter((x) => x.key !== key));
  const setAudioLabel = (key, label) => setAudios((a) => a.map((x) => (x.key === key ? { ...x, label } : x)));

  const addBanner = () => setState((s) => ({ ...s, banners: [...s.banners, { id: "b" + Date.now(), image_url: "", title: "", link: "", active: true, order: s.banners.length }] }));  const rmBanner = (id) => setState((s) => ({ ...s, banners: s.banners.filter((b) => b.id !== id) }));
  const setBanner = (id, k, v) => setState((s) => ({ ...s, banners: s.banners.map((b) => (b.id === id ? { ...b, [k]: v } : b)) }));

  return (
    <div>
      <PageHeader title="Customer CMS" subtitle="Content for the Customer Website (site_content) and the Customer App (config/app_settings, system_status). Every publish needs the profile password + OTP and is versioned." />
      <Tabs defaultValue="website">
        <TabsList className="mb-4">
          <TabsTrigger value="website" data-testid="cms-tab-website">Customer Website</TabsTrigger>
          <TabsTrigger value="app" data-testid="cms-tab-app">Customer App</TabsTrigger>
        </TabsList>
        <TabsContent value="website"><SiteContentEditor /></TabsContent>
        <TabsContent value="app">
      <div className="flex justify-end gap-2 mb-4">
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setVer("config")} data-testid="app-versions-btn"><Icons.History className="h-4 w-4" /> Versions</Button>
        <Button size="sm" className="gap-1.5" onClick={save} data-testid="cms-save"><Icons.UploadCloud className="h-4 w-4" /> Publish hero & banners</Button>
      </div>

      {/* HERO */}
      <Card className="p-5 border-border/60 mb-6" data-testid="hero-editor">
        <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2"><Icons.Image className="h-4 w-4 text-indigo-400" /> Hero Section</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <ImageField value={state.hero.image_url} spec={SPECS.appHero} folder="app_content/app" testId="hero"
              onChange={(url) => setState((s) => ({ ...s, hero: { ...s.hero, image_url: url } }))} />
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="hero-title" value={state.hero.title} onChange={(e) => setState((s) => ({ ...s, hero: { ...s.hero, title: e.target.value } }))} /></div>
            <div className="space-y-1.5"><Label>Subtitle</Label><Input data-testid="hero-subtitle" value={state.hero.subtitle} onChange={(e) => setState((s) => ({ ...s, hero: { ...s.hero, subtitle: e.target.value } }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>CTA text</Label><Input data-testid="hero-cta-text" value={state.hero.cta_text} onChange={(e) => setState((s) => ({ ...s, hero: { ...s.hero, cta_text: e.target.value } }))} /></div>
              <div className="space-y-1.5"><Label>CTA link</Label><Input data-testid="hero-cta-link" value={state.hero.cta_link} onChange={(e) => setState((s) => ({ ...s, hero: { ...s.hero, cta_link: e.target.value } }))} /></div>
            </div>
          </div>
        </div>
      </Card>

      {/* BANNERS */}
      <Card className="p-5 border-border/60" data-testid="banners-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-foreground flex items-center gap-2"><Icons.GalleryHorizontalEnd className="h-4 w-4 text-cyan-400" /> Carousel Banners</h3>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={addBanner} data-testid="add-banner"><Icons.Plus className="h-4 w-4" /> Add banner</Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {state.banners.map((b) => (
            <div key={b.id} className="rounded-lg border border-border p-4" data-testid={`banner-${b.id}`}>
              <div className="mb-3">
                <ImageField value={b.image_url} spec={SPECS.appBanner} folder="app_content/app" testId={`banner-${b.id}-img`}
                  onChange={(url) => setBanner(b.id, "image_url", url)} />
              </div>
              <div className="flex items-center justify-end gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Active</span>
                  <Switch checked={b.active} onCheckedChange={(v) => setBanner(b.id, "active", v)} data-testid={`banner-active-${b.id}`} />
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => rmBanner(b.id)} data-testid={`banner-remove-${b.id}`}><Icons.Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <Input className="mb-2" placeholder="Title" value={b.title} onChange={(e) => setBanner(b.id, "title", e.target.value)} data-testid={`banner-title-${b.id}`} />
              <Input placeholder="Link (deep link / URL)" value={b.link} onChange={(e) => setBanner(b.id, "link", e.target.value)} data-testid={`banner-link-${b.id}`} />
            </div>
          ))}
          {state.banners.length === 0 && <p className="text-sm text-muted-foreground col-span-2 py-6 text-center">No banners yet. Add one and publish.</p>}
        </div>
      </Card>

      {/* LIVE APP SETTINGS (system_status/global) */}
      <Card className="p-5 border-border/60 mt-6" data-testid="live-settings">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-foreground flex items-center gap-2"><Icons.ToggleRight className="h-4 w-4 text-emerald-500" /> Live App Settings</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setVer("system_status")} data-testid="sys-versions-btn"><Icons.History className="h-4 w-4" /> Versions</Button>
            <Button size="sm" className="gap-1.5" onClick={saveSys} data-testid="sys-publish"><Icons.UploadCloud className="h-4 w-4" /> Publish</Button>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div><p className="text-sm font-medium text-foreground">Show “pros notified” count to customers</p><p className="text-xs text-muted-foreground">When off, the Customer App hides how many pros were notified after a lead is floated.</p></div>
          <Switch checked={showPros} onCheckedChange={setShowPros} data-testid="toggle-pros-notified" />
        </div>
      </Card>

      {/* PRO APP AUDIO */}
      <Card className="p-5 border-border/60 mt-6" data-testid="pro-audio">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-foreground flex items-center gap-2"><Icons.Volume2 className="h-4 w-4 text-indigo-500" /> Pro App Sounds</h3>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={addAudio} data-testid="add-audio"><Icons.Plus className="h-4 w-4" /> Add sound</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Upload the audio each Pro app plays for these events. Published to <span className="font-mono">system_status/global.pro_audio</span> — click Publish above after changes.</p>
        <div className="space-y-3">
          {audios.map((a) => (
            <div key={a.key} className="rounded-lg border border-border p-3" data-testid={`audio-${a.key}`}>
              <div className="flex items-center gap-3">
                <Input value={a.label} onChange={(e) => setAudioLabel(a.key, e.target.value)} className="max-w-[220px]" data-testid={`audio-label-${a.key}`} />
                <span className="text-[11px] font-mono text-muted-foreground">{a.key}</span>
                <div className="ml-auto flex items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer rounded-md border border-border px-2 py-1.5 hover:bg-accent" data-testid={`audio-upload-label-${a.key}`}>
                    <Icons.Upload className="h-3.5 w-3.5" /> {audioUp === a.key ? "Uploading…" : (a.url ? "Replace" : "Upload")}
                    <input type="file" accept="audio/*" className="hidden" data-testid={`audio-upload-${a.key}`} onChange={(e) => uploadAudio(e.target.files[0], a.key)} />
                  </label>
                  {a.key !== "new_lead" && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => rmAudio(a.key)} data-testid={`audio-remove-${a.key}`}><Icons.Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
              {a.url ? <audio controls src={a.url} className="mt-2 w-full h-9" data-testid={`audio-player-${a.key}`} /> : <p className="mt-2 text-xs text-muted-foreground">No audio uploaded yet.</p>}
            </div>
          ))}
        </div>
      </Card>
        </TabsContent>
      </Tabs>
      <CmsPublishGate gate={gate} onClose={() => setGate(null)} />
      <VersionsSheet target={ver || "config"} docId={ver === "system_status" ? "global" : "app_settings"} open={!!ver} onClose={() => setVer(null)} />
    </div>
  );
}
